import React, { useState } from "react";
import { IconButton, Typography, Popover, Box, Button, TextField } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface MonthNavigatorProps {
  value: string; // in format "YYYY-MM"
  onMonthChange: (newMonth: string) => void;
}

const MonthNavigator: React.FC<MonthNavigatorProps> = ({ value, onMonthChange }) => {
  // Construct the currentDate in UTC by appending "T00:00:00Z"
  const currentDate = new Date(value + "-01T00:00:00Z");
  const today = new Date();
  // Create a UTC version of today's date (set to midnight UTC)
  const todayUTC = new Date(today.toISOString().slice(0, 10) + "T00:00:00Z");

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setUTCMonth(currentDate.getUTCMonth() - 1);
    onMonthChange(newDate.toISOString().slice(0, 7));
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setUTCMonth(currentDate.getUTCMonth() + 1);
    if (newDate > todayUTC) {
      return;
    }
    onMonthChange(newDate.toISOString().slice(0, 7));
  };

  const handleLabelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "month-picker-popover" : undefined;
  const disableForward = currentDate.toISOString().slice(0, 7) === todayUTC.toISOString().slice(0, 7);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton onClick={handlePreviousMonth}>
        <ArrowBack />
      </IconButton>
      {/* Fixed width container for the month label */}
      <Button
        onClick={handleLabelClick}
        sx={{
          textTransform: "none",
          minWidth: "160px",
          width: "160px",
          justifyContent: "center",
        }}
      >
        <Typography variant="subtitle1">
          {currentDate.toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
            timeZone: "UTC",
          })}
        </Typography>
      </Button>
      <IconButton onClick={handleNextMonth} disabled={disableForward}>
        <ArrowForward />
      </IconButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              openTo="month"
              views={["year", "month"]}
              // Use the UTC date for the picker as well.
              value={currentDate}
              onChange={(newDate) => {
                if (newDate) {
                  const selected = new Date(newDate.toISOString().slice(0, 10) + "T00:00:00Z");
                  onMonthChange(selected.toISOString().slice(0, 7));
                }
                handleClose();
              }}
              // This prevents the user from navigating to any date beyond today.
              maxDate={todayUTC}
              renderInput={(params) => <TextField {...params} style={{ display: 'none' }} />}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </Box>
  );
};

export default MonthNavigator;